import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    // Load auth user from localStorage on mount
    const [authUser, setAuthUser] = useState(() => {
        const stored = localStorage.getItem('authUser');
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                return null;
            }
        }
        return null;
    });

    // Save auth user to localStorage whenever it changes
    useEffect(() => {
        if (authUser) {
            localStorage.setItem('authUser', JSON.stringify(authUser));
        } else {
            localStorage.removeItem('authUser');
        }
    }, [authUser]);

    const login = async (email, password) => {
        try {
            const response = await apiService.login({ email, password });
            // console.log("returned response after logging in::", response.data.data);
            const { token } = response.data.data;

            if (token) {
                // Store token temporarily for OTP verification
                localStorage.setItem('tempToken', token);
                return { success: true, token };
            }
            return { success: false, error: 'Login failed' };
        } catch (error) {
            console.error('Login error:', error);
            return { success: false, error: error.response?.data?.message || 'Login failed' };
        }
    };

    const verifyOtp = async (otp) => {
        try {
            const token = localStorage.getItem('tempToken');
            if (!token) {
                return { success: false, error: 'No token found' };
            }
            const Otp = parseInt(otp);
            const response = await apiService.verifyOtp(token, Otp);
            const { user, accessToken } = response.data.data;

            if (user && accessToken) {
                // Set authenticated user
                setAuthUser(user);
                // Clean up temp token
                localStorage.removeItem('tempToken');
                // Store access token for API calls
                localStorage.setItem('authToken', accessToken);
                return { success: true, user };
            }
            return { success: false, error: 'OTP verification failed' };
        } catch (error) {
            console.error('OTP verification error:', error);
            return { success: false, error: error.response?.data?.message || 'OTP verification failed' };
        }
    };

    const logout = () => {
        setAuthUser(null);
        localStorage.removeItem('authUser');
        localStorage.removeItem('authToken');
        localStorage.removeItem('currentRole'); // Clean up old role storage
    };

    const value = {
        authUser,
        login,
        verifyOtp,
        logout,
        isAuthenticated: !!authUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

